import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { take } from 'rxjs';

import { TranslateModule } from '@ngx-translate/core';

import { ESelectedEntityType } from '@common/types/layout.type';

import {
    columnWidth as columnWidthOf,
    flexAlignVar,
    getSpecificSectionInnerCssClasses,
    numberWithUnitVar,
    sectionBgLayerVar,
    sectionBgVar,
    sectionGapHalfVar,
    sectionMaxWidthVar,
    spacingSideVar,
    textAlignVar,
} from '@sites/entity-core';
import type { SectionSpecificForm } from '@sites/entity-signal-form';

import type {
    DenormalizedColumnEntity,
    DenormalizedSpecificColumnEntity,
    DenormalizedSpecificBlockEntity,
    DenormalizedSpecificSectionEntity,
} from '@web/types';

import { environment } from '@web-env/environment';

import { BlocksFacade } from '@web/store/page-structure/blocks/blocks.facade';
import { ColumnsFacade } from '@web/store/page-structure/columns/columns.facade';
import { LayoutFacade } from '@web/store/layout/layout.facade';
import { SectionsFacade } from '@web/store/page-structure/sections/sections.facade';
import { NavigationDrawerService } from '@web/layout/drawers/navigation-drawer/navigation-drawer.service';
import { WebSectionFormService } from '@web/core/services/web-section-form.service';
import { WebColumnFormService } from '@web/core/services/web-column-form.service';

import { SpecificColumnComponent } from '@web/layout/content/page-content/components/columns/specific-column/specific-column.component';
import { SpecificSectionControlsComponent } from '@web/layout/content/page-content/components/controls/specific-section-controls/specific-section-controls.component';
import { WebColumnResizeComponent } from '@web/shared/components/web-column-resize/web-column-resize.component';
import { SECTION_CSS_RESOLVERS } from '@web/core/constants/css-resolvers.const';

const MAX_EQUAL_COLUMN_COUNT = 4;
const MAX_COLUMN_COUNT = 6;

@Component({
    selector: 'web-specific-section',
    templateUrl: './specific-section.component.html',
    styleUrls: ['./specific-section.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SpecificColumnComponent, WebColumnResizeComponent, SpecificSectionControlsComponent, TranslateModule],
})
// TODO: Refactor this component to extend BaseSectionComponent
export class SpecificSectionComponent implements OnInit {
    public readonly parentEntity = input.required<DenormalizedSpecificBlockEntity>();
    public readonly entity = input.required<DenormalizedSpecificSectionEntity>();
    public readonly index = input.required<number>();
    public readonly isFirst = input<boolean>(false);
    public readonly isLast = input<boolean>(false);
    public readonly blockHasLogo = input<boolean>(false);

    private readonly columnsFacade = inject(ColumnsFacade);
    private readonly sectionsFacade = inject(SectionsFacade);
    private readonly blocksFacade = inject(BlocksFacade);
    private readonly layoutFacade = inject(LayoutFacade);
    private readonly navDrawerService = inject(NavigationDrawerService);
    private readonly sectionFormService = inject(WebSectionFormService);
    private readonly columnFormService = inject(WebColumnFormService);
    private readonly destroyRef = inject(DestroyRef);

    /**
     * Styling is read from the signal form rather than the entity input, the way
     * `default-tab` does it: the `data` and `styles` bags are separate signals, so
     * a padding edit no longer recomputes the background and vice versa.
     */
    private form!: SectionSpecificForm;

    public readonly hideControls = toSignal(this.layoutFacade.hideControls$, { initialValue: false });
    private readonly selectedEntityId = toSignal(this.layoutFacade.entityId$);

    public readonly isActive = computed<boolean>(() => this.selectedEntityId() === this.entity().id);

    public readonly isShowMoreActionState = signal<boolean>(false);

    /** Only the slots the row is configured to show, in left → center → right order. */
    public readonly columns = computed<DenormalizedSpecificColumnEntity[]>(() => {
        const { props, children } = this.entity();
        const visible: DenormalizedSpecificColumnEntity[] = [];

        if (props.slots.left && children[0]) visible.push(children[0]);
        if (props.slots.center && children[1]) visible.push(children[1]);
        if (props.slots.right && children[2]) visible.push(children[2]);

        return visible;
    });

    public readonly columnCount = computed<number>(() => this.columns().length);
    public readonly columnsCountReachedMax = computed<boolean>(() => this.columnCount() >= MAX_COLUMN_COUNT);

    public readonly sectionColumnGap = computed<number>(() => this.entity().props.columnGap.value);
    public readonly disableDeleteEvent = computed<boolean>(() => this.parentEntity().children.length === 1);

    // Every read below is a single FieldTree leaf, so a padding edit no longer
    // recomputes the background and vice versa.
    private readonly backgroundImage = computed(() => this.form.backgroundImage().value());
    private readonly padding = computed(() => this.form.padding().value());
    private readonly columnGap = computed(() => ({ value: this.form.columnGap().value(), unit: 'px' as const }));

    /** Styling of the row — see `section.base.less`. */
    public readonly bg = computed<string>(() =>
        sectionBgVar(this.form.backgroundColor().value(), this.backgroundImage(), SECTION_CSS_RESOLVERS)
    );
    public readonly bgPosition = computed(() =>
        sectionBgLayerVar(this.backgroundImage(), this.form.backgroundPosition().value())
    );
    public readonly bgRepeat = computed(() =>
        sectionBgLayerVar(this.backgroundImage(), this.form.backgroundRepeat().value())
    );
    public readonly bgSize = computed(() =>
        sectionBgLayerVar(this.backgroundImage(), this.form.backgroundSize().value())
    );

    public readonly maxWidth = computed(() => sectionMaxWidthVar(this.parentEntity().props.width));
    public readonly alignItems = computed(() => flexAlignVar(this.form.verticalPosition().value()));
    public readonly gap = computed(() => numberWithUnitVar(this.columnGap(), '0px'));
    public readonly gapHalf = computed(() => sectionGapHalfVar(this.columnGap()));
    /** Vertical margin around each slot; see `slotVerticalMargin` on the schema. */
    public readonly slotMarginHalf = computed(() =>
        sectionGapHalfVar({ value: this.form.slotVerticalMargin().value(), unit: 'px' })
    );
    public readonly paddingTop = computed(() => spacingSideVar(this.padding(), 'top'));
    public readonly paddingBottom = computed(() => spacingSideVar(this.padding(), 'bottom'));
    public readonly paddingRight = computed(() => spacingSideVar(this.padding(), 'right'));
    public readonly paddingLeft = computed(() => spacingSideVar(this.padding(), 'left'));
    public readonly mobileTextAlign = computed(() => textAlignVar(this.parentEntity().props.horizontalPosition));

    public readonly sectionInnerClasses = computed<string>(() =>
        getSpecificSectionInnerCssClasses(this.entity(), this.parentEntity(), true)
    );

    public ngOnInit(): void {
        // The registry dispatches on the persisted variant; this host is only ever
        // mounted for header rows, so the specific branch is the one it gets.
        this.form = this.sectionFormService.getOrCreateScoped(this.entity().id, this.destroyRef) as SectionSpecificForm;
    }

    public selectSection(): void {
        this.layoutFacade.setSelectedEntity({
            entityType: ESelectedEntityType.specificSection,
            entityId: this.entity().id,
        });
        this.navDrawerService.selectInNavigation({ entityId: this.entity().id, blockId: this.parentEntity().id });
    }

    public addSection(): void {
        this.sectionsFacade.createSpecificSection(this.entity().parentId, this.index() + 1);
    }

    public moveUp(): void {
        this.sectionsFacade.moveUp(this.index(), this.entity().parentId);
    }

    public moveDown(): void {
        this.sectionsFacade.moveDown(this.index(), this.entity().parentId);
    }

    public removeSection(): void {
        const block = this.parentEntity();

        if (block.children.length === 1) {
            this.blocksFacade.blocksIds$.pipe(take(1)).subscribe((blockIds) => {
                const index = blockIds.indexOf(block.id);
                if (index === -1) {
                    console.error('Block not found');
                    return;
                }
                this.blocksFacade.removeBlock(block, index);
            });
            return;
        }

        this.sectionsFacade.removeSection(this.entity(), this.index());
    }

    /** One entry per slot this row has resized — see {@link columnForm}. */
    private readonly columnForms = new Map<string, ReturnType<WebColumnFormService['getOrCreateScoped']>>();

    /**
     * Scoped to the row, so each slot's form is ref-counted once rather than on
     * every step of a drag.
     */
    private columnForm(id: string) {
        const existing = this.columnForms.get(id);
        if (existing) {
            return existing;
        }

        const form = this.columnFormService.getOrCreateScoped(id, this.destroyRef);
        this.columnForms.set(id, form);

        return form;
    }

    /**
     * Moves one of the twelve units from one header slot to its neighbour, through
     * that slot's own form. Dispatching the two updates directly, as this used to,
     * left both slots' forms holding the pre-drag widths — and recorded no history
     * at all, so a header resize could not be undone.
     */
    private moveWidth(column: DenormalizedSpecificColumnEntity, delta: number): void {
        const form = this.columnForm(column.id);

        if ('setWidth' in form) {
            form.setWidth(column.props.width.value + delta);
        }
    }

    public changeColumnWidthToLeft(
        colLeft: DenormalizedSpecificColumnEntity,
        colRight: DenormalizedSpecificColumnEntity
    ): void {
        this.moveWidth(colLeft, -1);
        this.moveWidth(colRight, 1);
    }

    public changeColumnWidthToRight(
        colLeft: DenormalizedSpecificColumnEntity,
        colRight: DenormalizedSpecificColumnEntity
    ): void {
        this.moveWidth(colLeft, 1);
        this.moveWidth(colRight, -1);
    }

    public getColumnClasses(column: DenormalizedColumnEntity): string {
        const columnCount = this.columnCount();

        if (columnCount > MAX_EQUAL_COLUMN_COUNT) {
            return `lpc-col-equal-${columnCount}`;
        }

        const columnWidth = columnWidthOf(column) || 12 / columnCount;
        return `lpc-col-${columnWidth} lpc-col-equal-${columnCount}`;
    }

    /**
     * Readout for the resize badge. Header columns are sized in twelfths of the
     * grid — the same number `getColumnClasses` turns into `lpc-col-*` — so the
     * badge shows how many of the twelve the column currently takes.
     */
    public columnWidthLabel(column: DenormalizedColumnEntity): string {
        const columnWidth = columnWidthOf(column) || 12 / this.columnCount();

        return `${Math.round(columnWidth)}/12`;
    }

    public setMoreActionState(state: boolean): void {
        this.isShowMoreActionState.set(state);
    }
}
